import React, { useEffect } from 'react';
import { Header, Footer } from '@spotify-internal/masthead-react';

import ComponentConfiguration from '../../configuration';
import type {
  getHeader,
  getMasthead,
} from '../../../shared-server/api/www-masthead-api/www-masthead-api';
import I18nManager from '../I18nManager';
import '@spotify-internal/masthead-react/index.css';

import type { TextDirection } from '../../../shared-server/api/country-attributes/country-attributes';
import type { getUserData } from '../../lib/getUserData';
import type { getDeviceData } from '../../lib/getDeviceData';

import { usePublicRuntimeConfigValue } from '../../lib/config';
import { track } from '@spotify-internal/track-js';

import { Configuration } from '@spotify-internal/remote-config-properties';
import { NextJsRemoteConfigProvider } from '@spotify-internal/remote-config-integration-nextjs';
import { getTransport } from '../../lib/transport';
import { pageLoadEvent } from '../../lib/tracking/eventPayloadDefinitions';
import { trackEvent } from '../../lib/tracking';
import { getCookie } from '../../lib/cookie-utils';
import { legacyTracking } from '../../lib/tracking/legacy-tracking';
import { ValidThemes } from '../../configuration/theme';

import type { JsonRemoteConfig } from '../../../shared-server/lib/remote-config/get';

type TrackJsConfig = {
  enabled: boolean;
  googleTagManagerId?: string;
};

export type SchedulerData = {
  metadata: {
    title: string;
    description: string;
    image: string;
  };
  /**
   * component definitions are in toolbelt:
   * - marketing: https://ghe.spotify.net/headliner/premium-toolbelt/blob/master/packages/frontend/src/plugins/editor/schemas/web/marketingSchema.json
   * - premium consideration: https://ghe.spotify.net/headliner/premium-toolbelt/blob/master/packages/frontend/src/plugins/editor/schemas/web/premiumConsiderationSchema.json
   */
  components: any[];
};

export type BasePageProps = {
  masthead: {
    header?: Awaited<ReturnType<typeof getHeader>> | null;
    footer?: Awaited<ReturnType<typeof getMasthead>>['footer'] | null;
  } | null;
  country: string;
  direction: TextDirection;
  language: string;
  market: string;
  oneTrust: boolean;
  showAsWebView: boolean;
  userData: ReturnType<typeof getUserData>;
  deviceData: ReturnType<typeof getDeviceData>;
  themeName?: ValidThemes | 'encoreLight';
  remoteConfig: JsonRemoteConfig;
};

export function BasePage({
  basePageProps: {
    masthead,
    country,
    language,
    direction,
    market,
    oneTrust,
    userData,
    deviceData,
    themeName,
    remoteConfig,
  },
  isEvergreen,
  eligibility,
  children,
  pageName,
  eventParams = {},
  variant,
}: {
  basePageProps: BasePageProps;
  isEvergreen: boolean;
  eligibility: string;
  children: React.ReactNode;
  pageName: string;
  eventParams?: {
    campaignId?: string;
    offerTypeId?: string;
  };
  variant?: string;
}) {
  const transport = getTransport();

  useEffect(() => {
    if (
      !transport.isConnected() &&
      window.location.host === 'www.spotify.com'
    ) {
      transport.connect();
    }
  }, [transport]);

  const { enabled: trackJsEnabled, googleTagManagerId } =
    usePublicRuntimeConfigValue<TrackJsConfig>('analytics.trackJs');

  const userDetails: typeof userData & { eligibility: string } = {
    ...userData,
    eligibility,
  };

  useEffect(() => {
    if (!trackJsEnabled) {
      return;
    }

    track({
      market,
      language,
      googleTagManagerId,
      oneTrust,
    });
  }, [
    oneTrust,
    googleTagManagerId,
    language,
    market,
    trackJsEnabled,
    country,
    userData.authenticated,
  ]);

  useEffect(() => {
    const httpReferer = document.referrer;
    const baseElement = document.getElementById('__next');

    if (baseElement) {
      const payload = {
        resource_name: pageName,
        country: country ?? 'unknown',
        locale: language ?? 'unknown',
        full_url: window.location.href,
        correlation_id: getCookie('sp_t') ?? 'unknown',
        is_evergreen: isEvergreen,
        eligibility: eligibility,
        httpReferer: httpReferer,
        campaign_id: eventParams.campaignId,
        offer_type_id: eventParams.offerTypeId,
      };

      trackEvent(
        pageLoadEvent(payload, {
          authorize: userData?.authenticated,
        }),
      );

      if (variant === 'control') {
        legacyTracking({
          analyticsRoot: baseElement,
          resourceName: pageName,
          language,
          country,
          isEvergreen,
          eligibility,
          authenticated: userData.authenticated,
        });
      }
    }
  }, [
    country,
    language,
    userData.authenticated,
    eligibility,
    isEvergreen,
    pageName,
    eventParams,
    variant,
  ]);

  return (
    <NextJsRemoteConfigProvider
      context={{ username: null }}
      options={{
        transport,
        clientId: 'www-marketing',
        timeout: 5000,
        onDestroy: () => {},
        initialConfiguration: Configuration.fromJSON(remoteConfig),
      }}
    >
      <I18nManager
        language={language ? language.replace('_', '-') : 'en'}
        direction={direction}
      >
        {masthead?.header && (
          <Header
            fixed={false}
            transparent={false}
            {...(masthead.header as React.ComponentPropsWithoutRef<
              typeof Header
            >)}
          />
        )}
        <ComponentConfiguration
          direction={direction}
          market={{ market, country, language, isEvergreen }}
          themeName={!!themeName ? themeName : 'encoreDark'}
          user={userDetails}
          device={deviceData}
        >
          {children}
        </ComponentConfiguration>
        {masthead?.footer && (
          <Footer
            {...(masthead.footer as React.ComponentPropsWithoutRef<
              typeof Footer
            >)}
          />
        )}
      </I18nManager>
    </NextJsRemoteConfigProvider>
  );
}

export default BasePage;
